import { useState, useEffect } from "react";
import { Tooltip } from "antd";
import {
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
} from "@ant-design/icons";
import styles from "./FeedbackSection.module.less";
import { FeedbackSummary } from "~/types/feedback";
import { FEEDBACK_EVENTS } from "~/constants/events";
import { AddingFeedbackInput } from "~/pages/copilotMessage/types/copilot";
import { addFeedback } from "~/pages/copilotMessage/services/copilot";
import { FeedbackRateEnum } from "~/constants/feedback";
import { useLanguage } from "~/hooks/useLanguage";

export interface FeedbackSectionProps {
  messageId: string;
  conversationId: string;
  feedbackSummary?: FeedbackSummary;
}

const FeedbackSection: React.FC<FeedbackSectionProps> = ({
  messageId,
  conversationId,
  feedbackSummary,
}) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackRateEnum | null>(
    null,
  );

  const { formatMessage } = useLanguage();

  const [likeCount, setLikeCount] = useState<number>(0);
  const [dislikeCount, setDislikeCount] = useState<number>(0);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (feedbackSummary) {
      setLikeCount(
        feedbackSummary.rateCounts.find((r) => r.rate === FeedbackRateEnum.LIKE)
          ?.count || 0,
      );
      setDislikeCount(
        feedbackSummary.rateCounts.find(
          (r) => r.rate === FeedbackRateEnum.DISLIKE,
        )?.count || 0,
      );
    }

    if (feedbackSummary && feedbackSummary.currentUserRate !== null) {
      setFeedbackType(feedbackSummary.currentUserRate as FeedbackRateEnum);
    }
  }, [feedbackSummary]);

  // Listen for feedback submitted events
  useEffect(() => {
    const handleFeedbackSubmitted = () => {
      setIsSubmitted(true);
    };

    window.addEventListener(
      FEEDBACK_EVENTS.FEEDBACK_SUBMITTED,
      handleFeedbackSubmitted as EventListener,
    );

    return () => {
      window.removeEventListener(
        FEEDBACK_EVENTS.FEEDBACK_SUBMITTED,
        handleFeedbackSubmitted as EventListener,
      );
    };
  }, [messageId, conversationId]);

  // Listen for feedback cancelled events
  useEffect(() => {
    const handleFeedbackCancelled = () => {
      // Only revert feedback if it hasn't handleFeedbackbeen submitted yet
      if (!isSubmitted && feedbackType != undefined) {
        // Revert the count based on current feedback type
        if (feedbackType === FeedbackRateEnum.LIKE) {
          setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (feedbackType === FeedbackRateEnum.DISLIKE) {
          setDislikeCount((prev) => (prev > 0 ? prev - 1 : 0));
        }

        // Reset feedback type
        setFeedbackType(null);
      }
    };

    window.addEventListener(
      FEEDBACK_EVENTS.FEEDBACK_CANCELLED,
      handleFeedbackCancelled as EventListener,
    );

    return () => {
      window.removeEventListener(
        FEEDBACK_EVENTS.FEEDBACK_CANCELLED,
        handleFeedbackCancelled as EventListener,
      );
    };
  }, [messageId, isSubmitted, feedbackType]);

  const handleFeedback = async (type: FeedbackRateEnum) => {
    // Check if user is reverting their existing feedback (toggle off)
    if (feedbackType === type) {
      // User is reverting their existing feedback
      const feedbackInput: AddingFeedbackInput = {
        CopilotConversationId: conversationId,
        CopilotResponseId: messageId,
        DefinitionId: undefined,
        Description: "",
      };

      try {
        await addFeedback(feedbackInput);
        setIsSubmitted(false);
      } catch (error) {
        // throw error as Error;
      } finally {
        setFeedbackType(null);
        if (type === FeedbackRateEnum.LIKE) {
          setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (type === FeedbackRateEnum.DISLIKE) {
          setDislikeCount((prev) => (prev > 0 ? prev - 1 : 0));
        }

        const event = new CustomEvent(FEEDBACK_EVENTS.CHAT_CLOSED);
        window.dispatchEvent(event);
      }

      return;
    }

    // User is giving new feedback or changing feedback
    if (type === FeedbackRateEnum.LIKE) {
      // If user had dislike before, decrease dislike count
      if (feedbackType === FeedbackRateEnum.DISLIKE) {
        setDislikeCount((prev) => (prev > 0 ? prev - 1 : 0));
      }

      // Always open chat for feedback options when user clicks like
      // This ensures chatbox opens even after page reload
      const event = new CustomEvent(FEEDBACK_EVENTS.FEEDBACK_LIKE, {
        detail: {
          messageId,
          conversationId,
          type: "like",
        },
      });
      window.dispatchEvent(event);

      setLikeCount((prev) => prev + 1);
      setFeedbackType(FeedbackRateEnum.LIKE);
    } else if (type === FeedbackRateEnum.DISLIKE) {
      // If user had like before, decrease like count
      if (feedbackType === FeedbackRateEnum.LIKE) {
        setLikeCount((prev) => (prev > 0 ? prev - 1 : 0));
      }

      // Always open chat for feedback options when user clicks dislike
      // This ensures chatbox opens even after page reload
      const event = new CustomEvent(FEEDBACK_EVENTS.FEEDBACK_DISLIKE, {
        detail: {
          messageId,
          conversationId,
          type: "dislike",
        },
      });
      window.dispatchEvent(event);

      setDislikeCount((prev) => prev + 1);
      setFeedbackType(FeedbackRateEnum.DISLIKE);
    }
  };

  return (
    <div className={styles.feedbackSection}>
      <div className={styles.feedbackPocket}>
        <div className={styles.feedbackButtonsContainer}>
          <Tooltip title={formatMessage("FeedbackSection.ThisWasHelpful")}>
            <span
              className={styles.feedbackButton}
              onClick={() => handleFeedback(FeedbackRateEnum.LIKE)}
              tabIndex={0}
              aria-label="Like"
              onKeyDown={(e) =>
                e.key === "Enter" && handleFeedback(FeedbackRateEnum.LIKE)
              }
              style={
                feedbackType === FeedbackRateEnum.LIKE
                  ? {
                      color: "#2ecc71",
                      backgroundColor: "rgba(46, 204, 113, 0.1)",
                    }
                  : {}
              }
            >
              {feedbackType === FeedbackRateEnum.LIKE ? (
                <LikeFilled />
              ) : (
                <LikeOutlined />
              )}
              <span
                className={styles.feedbackCount}
                style={{
                  color: "#2ecc71",
                  marginLeft: 6,
                  fontWeight: 500,
                  fontSize: 14,
                }}
              >
                {likeCount}
              </span>
            </span>
          </Tooltip>
          <Tooltip title={formatMessage("FeedbackSection.ThisWasntHelpful")}>
            <span
              className={styles.feedbackButton}
              onClick={() => handleFeedback(FeedbackRateEnum.DISLIKE)}
              tabIndex={0}
              aria-label="Dislike"
              onKeyDown={(e) =>
                e.key === "Enter" && handleFeedback(FeedbackRateEnum.DISLIKE)
              }
              style={
                feedbackType === FeedbackRateEnum.DISLIKE
                  ? {
                      color: "#e74c3c",
                      backgroundColor: "rgba(231, 76, 60, 0.1)",
                    }
                  : {}
              }
            >
              {feedbackType === FeedbackRateEnum.DISLIKE ? (
                <DislikeFilled />
              ) : (
                <DislikeOutlined />
              )}
              <span
                className={styles.feedbackCount}
                style={{
                  color: "#e74c3c",
                  marginLeft: 6,
                  fontWeight: 500,
                  fontSize: 14,
                }}
              >
                {dislikeCount}
              </span>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default FeedbackSection;
